import {
  Row,
  Descriptions,
  Button,
  Card,
  Col,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Popover,
} from "antd";
import { useFetchAllLawsuitsQuery } from "features/legal/legalAPI";
import { formatCurrency, formatDate, capitalizeFirstLetter, insertIf } from "common/utils";
import currency from "currency.js";
import { useMemo, useCallback } from "react";
import styled from "styled-components";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { collectorFullName } from "features/home/utils";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import {
  collectorsWithRolesSelector,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { useNavigate, useParams } from "react-router-dom";

const StyledInfoValue = styled.div`
  margin-left: 4px;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledTag = styled(Tag)`
  margin: 4px 4px;
`;

const StyledTable = styled(Table)`
  cursor: pointer;
  width: 100%;

  & .ant-table-cell:not(th) {
    padding: 4px 8px !important;
  }
`;

export default function PacketedLawsuitsTable({ packetId }) {
  const { debtorId } = useParams();
  const { data: packetedLawsuits = [], isLoading: isLoadingLawsuits } = useFetchAllLawsuitsQuery(
    {
      filters: {
        debtorPacketIds: [packetId],
      },
    },
    {
      skip: !packetId,
    },
  );
  const { data: creditors, isLoading: isLoadingCreditors } = useFetchCreditorSummariesQuery();
  const { collectorsWithRoles: collectors, isLoading: isLoadingCollectors } =
    useFetchCollectorsQuery(undefined, {
      selectFromResult: (result) => ({
        ...result,
        collectorsWithRoles: collectorsWithRolesSelector(result),
      }),
    });

  const creditorIdToCreditorMap = useMemo(() => {
    return creditors?.reduce((acc, creditor) => {
      acc[creditor.id] = creditor;
      return acc;
    }, {});
  }, [creditors]);

  const navigate = useNavigate();

  const handleDebtorClick = (id) => {
    window.open(`/debtors/${id}`, "_blank");
  };

  /**
   * Returns non-archived original accounts + all judgment accounts.
   * Matches backend's Lawsuit.legal_balance property logic.
   * For total balance display, prefer using lawsuit.legalBalance directly.
   */
  const getAccountsForLawsuit = (lawsuit) => {
    const nonArchivedOriginalAccounts = (lawsuit.accounts || []).filter(
      (account) =>
        account.legalStatus !== "in_litigation_archived" &&
        account.legalStatus !== "judgment_applied_archived",
    );
    return [...nonArchivedOriginalAccounts, ...(lawsuit.judgmentAccounts || [])];
  };

  // Calculate packet totals
  const packetTotals = useMemo(() => {
    if (!packetedLawsuits) return { totalLawsuits: 0, totalLawsuitsBalance: 0 };

    const totalLawsuits = packetedLawsuits.length || 0;
    const totalLawsuitsBalance = formatCurrency(
      packetedLawsuits.reduce((sum, lawsuit) => sum.add(lawsuit.legalBalance), currency(0)) ||
        currency(0),
    );

    return { totalLawsuits, totalLawsuitsBalance };
  }, [packetedLawsuits]);

  const lawsuitColumns = [
    {
      title: "Legal Case Number",
      dataIndex: "legalCaseNumber",
      key: "legalCaseNumber",
      render: (text) => text || "-",
    },
    {
      title: "Account IDs",
      key: "accountIds",
      width: 150,
      render: (text, record) => {
        const accounts = getAccountsForLawsuit(record);
        return accounts.map((account) => account.externalId || account.id).join(", ");
      },
    },
    {
      title: "Debtor IDs",
      key: "debtorIds",
      width: 150,
      render: (text, record) => {
        const debtors = record.debtors || [];
        return debtors.map((debtor) => (
          <Button type="link" key={debtor.id} onClick={() => handleDebtorClick(debtor.id)}>
            {debtor.name ? `${debtor.name} (${debtor.id})` : ""}
          </Button>
        ));
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => {
        if (record.status === "judgment") {
          return <Tag color="green">JUDGMENT APPLIED</Tag>;
        }
        if (record.status === "judgment_applied") {
          return <Tag color="green">JUDGMENT APPLIED</Tag>;
        }
        if (record.status === "in_litigation") {
          return <Tag color="orange">IN LITIGATION</Tag>;
        }
        if (record.status === "partial_judgment") {
          return <Tag color="orange">PARTIAL JUDGMENT</Tag>;
        }
        return text || "-";
      },
    },
    {
      title: "Total Balance",
      key: "totalAccountBalance",
      render: (text, record) => {
        const accounts = getAccountsForLawsuit(record);
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(
              (accounts ?? []).reduce(
                (sum, account) => sum.add(account.paymentCategories.principalBalance),
                currency(0),
              ),
            ),
          },
          {
            key: "interest",
            label: "Interest",
            value: formatCurrency(
              accounts.reduce(
                (sum, account) => sum.add(account.paymentCategories.interestBalance),
                currency(0),
              ),
            ),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(
              accounts.reduce(
                (sum, account) => sum.add(account.paymentCategories.clientInterestBalance),
                currency(0),
              ),
            ),
          },
          {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(
              accounts.reduce(
                (sum, account) => sum.add(account.paymentCategories.prejudgmentInterestBalance),
                currency(0),
              ),
            ),
          },
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(
              accounts.reduce(
                (sum, account) => sum.add(account.paymentCategories.totalFeesBalance),
                currency(0),
              ),
            ),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.legalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>{formatCurrency(record.legalBalance)}</StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "Filing Date",
      dataIndex: "filingDate",
      key: "filingDate",
      render: (text) => formatDate(text) || "-",
    },
  ];

  const expandableColumns = [
    {
      title: "Account ID",
      dataIndex: "id",
      key: "externalId",
      render: (text, record) => record.externalId ?? "-",
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (text, record) => (
        <Col>
          {record?.isClosed ? <StyledTag color="red">CLOSED</StyledTag> : null}
          {record?.legalStatus === "judgment" || record?.legalStatus === "judgment_applied" ? (
            <StyledTag color="green">JUDGMENT APPLIED</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment_applied_archived" ? (
            <StyledTag color="green">JUDGMENT ARCHIVED</StyledTag>
          ) : null}
          {record?.legalStatus === "in_litigation" ? (
            <StyledTag color="orange">IN LITIGATION</StyledTag>
          ) : null}
        </Col>
      ),
    },
    {
      title: "Judgment Date",
      dataIndex: "judgmentDate",
      key: "judgmentDate",
      render: (text, record) => formatDate(record.judgmentDate) ?? "-",
    },
    {
      title: "Current Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text, record) => {
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(record.paymentCategories.principalBalance),
          },
          {
            key: "interest",
            label: "Interest",
            value: formatCurrency(record.paymentCategories.interestBalance),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(record.paymentCategories.clientInterestBalance),
          },
          ...insertIf(record.legalStatus === "judgment", {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(record.paymentCategories.prejudgmentInterestBalance),
          }),
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(record.paymentCategories.totalFeesBalance),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.paymentCategories.totalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>
              {formatCurrency(record.paymentCategories.totalBalance)}
            </StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (text, record) => {
        const creditor = creditorIdToCreditorMap[record.creditorId];
        if (creditor?.code) {
          return `${creditor?.name} (${creditor?.code})`;
        }
        return creditor?.name ?? "-";
      },
    },
    {
      title: "Current Creditor",
      dataIndex: "currentCreditor",
      key: "currentCreditor",
      render: (text, record) => record.currentCreditorName ?? "-",
    },
    {
      title: "Co-Debtors",
      dataIndex: "coDebtors",
      key: "coDebtors",
      render: (text, record) => {
        const codebtorIds = record.debtorIds?.filter((id) => id.toString() !== debtorId) ?? [];

        if (codebtorIds.length === 0) {
          return "-";
        }

        return (
          <Col>
            {codebtorIds.map((codebtorId) => (
              <div key={codebtorId}>
                <Button
                  type="link"
                  onClick={() => {
                    navigate(`/debtors/${codebtorId}`);
                  }}
                >
                  {/* NOTE: We don't pull these codebtor names, because it'd be too expensive of a query on the backend. */}
                  {codebtorId}
                </Button>
              </div>
            ))}
          </Col>
        );
      },
    },
    {
      title: "Collector",
      dataIndex: "collector",
      key: "collector",
      render: (text, record) =>
        capitalizeFirstLetter(
          collectorFullName(collectors?.find((x) => x.id === record.assigneeId)),
        ),
    },
  ];

  const ExpandedRow = useCallback(
    (record) => {
      const accounts = getAccountsForLawsuit(record);

      return (
        <>
          <h4>Related Accounts</h4>
          <StyledTable
            bordered
            scroll={{ x: "max-content" }}
            // @ts-ignore
            columns={expandableColumns}
            dataSource={accounts}
            pagination={false}
            loading={isLoadingLawsuits || isLoadingCreditors || isLoadingCollectors}
          />
        </>
      );
    },
    // we don't want columns to be a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoadingLawsuits, packetedLawsuits],
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {isLoadingLawsuits ? (
        <Skeleton loading={isLoadingLawsuits} active />
      ) : (
        <Card>
          <Space direction="horizontal" align="center" size="large">
            <Statistic title="Total Lawsuits" value={packetTotals?.totalLawsuits || 0} />
            <Statistic title="Total Lawsuit Balance" value={packetTotals?.totalLawsuitsBalance} />
          </Space>
        </Card>
      )}
      <Table
        loading={isLoadingLawsuits || isLoadingCreditors || isLoadingCollectors}
        columns={lawsuitColumns}
        dataSource={packetedLawsuits}
        rowKey="id"
        bordered
        scroll={{ x: "max-content" }}
        pagination={{ defaultPageSize: 100 }}
        expandable={{
          expandedRowRender: ExpandedRow,
        }}
      />
    </Space>
  );
}
